import { Box, Paper, Stack, Typography, alpha } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CircularProgress from "@mui/material/CircularProgress";

const DEFAULT_BRAND = {
  main: "#2471A3",
  dark: "#1B4F72",
  light: "#EAF2F8",
  accent: "#F39C12",
  surface: "#F4F7F9",
  border: "#E1E8ED",
  warning: "#D68910",
  warningLight: "#FEF9E7",
};

function AlertaRemitosFacturacion({ 
  clienteNombre, 
  BRAND = DEFAULT_BRAND, 
  loadingRemitos, 
  faltaFacturacion = false, // true si falta el dato "Facturar a"
  onIrAPerfil 
}) {
  const brandColors = { ...DEFAULT_BRAND, ...BRAND };

  const config = faltaFacturacion ? {
    gradient: `linear-gradient(90deg, ${brandColors.accent} 0%, ${brandColors.warning} 100%)`,
    iconBg: brandColors.warningLight,
    iconBorder: alpha(brandColors.accent, 0.3),
    IconComponent: WarningAmberOutlinedIcon,
    iconColor: brandColors.warning,
    title: "Datos de facturación incompletos",
    mensaje: clienteNombre ? (
      <>
        El cliente{" "}
        <Box component="span" sx={{ fontWeight: 700, color: brandColors.main }}>
          {clienteNombre.trim()}
        </Box>{" "}
        no tiene definido el campo <strong>"Facturar a"</strong>.
      </>
    ) : (
      <>Este cliente no tiene definido el campo <strong>"Facturar a"</strong>.</>
    ),
    badgeBg: alpha(brandColors.accent, 0.12),
    badgeIcon: EditNoteIcon,
    badgeColor: "#B9770E",
    badgeText: 'Completá el dato "Facturar a" en el perfil del cliente y volvé a intentar.',
    isClickable: Boolean(onIrAPerfil),
  } : {
    gradient: `linear-gradient(90deg, ${brandColors.main} 0%, ${brandColors.dark} 100%)`,
    iconBg: brandColors.light,
    iconBorder: alpha(brandColors.main, 0.25),
    IconComponent: Inventory2OutlinedIcon,
    iconColor: brandColors.main,
    title: "No hay registros",
    mensaje: clienteNombre ? (
      <>
        El cliente{" "}
        <Box component="span" sx={{ fontWeight: 700, color: brandColors.main }}>
          {clienteNombre.trim()}
        </Box>{" "}
        no tiene pedidos / remitos pendientes de facturación.
      </>
    ) : (
      "Este cliente no tiene pedidos / remitos pendientes de facturación."
    ),
    badgeBg: alpha(brandColors.accent, 0.12),
    badgeIcon: PersonSearchIcon,
    badgeColor: "#B9770E",
    badgeText: "Seleccioná otro cliente para continuar..",
    isClickable: false,
  };

  const IconToRender = config.IconComponent;
  const BadgeIconToRender = config.badgeIcon;

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          width: "100%",
          borderRadius: 3,
          p: { xs: 2, sm: 2.5 },
          textAlign: "left",
          border: `1px solid ${brandColors.border}`,
          bgcolor: "#ffffff",
          boxShadow: "0 1px 3px rgba(16,42,67,0.06)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 6,
            background: config.gradient,
          }}
        />

        {loadingRemitos ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              py: 1.5,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: brandColors.main }} size={32} />
            <Typography variant="body2" sx={{ color: brandColors.dark, fontWeight: 500 }}>
              Buscando remitos/pedidos sin facturar...
            </Typography>
          </Box>
        ) : (
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                flexShrink: 0,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: config.iconBg,
                border: `1px solid ${config.iconBorder}`,
              }}
            >
              <IconToRender sx={{ fontSize: 36, color: config.iconColor }} />
            </Box>

            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{ fontWeight: 700, mb: 0.5, color: brandColors.dark }}
              >
                {config.title}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.4,
                  color: "#5B6B7A",
                  mb: 1,
                }}
              >
                {config.mensaje}
              </Typography>

              <Box
                onClick={config.isClickable ? onIrAPerfil : undefined}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: config.badgeBg,
                  cursor: config.isClickable ? "pointer" : "default",
                  transition: "background-color 0.2s ease",
                  "&:hover": config.isClickable
                    ? { bgcolor: alpha(brandColors.accent, 0.22) }
                    : {},
                }}
              >
                <BadgeIconToRender sx={{ fontSize: 18, color: config.badgeColor }} />
                <Typography
                  variant="caption"
                  sx={{ color: config.badgeColor, fontWeight: 600 }}
                >
                  {config.badgeText}
                </Typography>
              </Box>
            </Box>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}

export default AlertaRemitosFacturacion;